import ModalConfigCookies from '@design-system/modalconfigcookies'
import type { CookieCard } from '@design-system/modalconfigcookies'

import { useLocale } from 'context/LocaleContext'
import { useCookieConsentPropsStore } from 'store/useCookieConsentProps'
import type { ICookieConsentCard } from 'store/useCookieConsentProps'
import { useCookieConsentStore } from 'store/useCookieConsentStore'

import { useCookieConsent } from 'hooks/cookieConsent/useCookieConsent'

/**
 * Fallback texts per brand for the preferences center. Same pattern as the
 * banner: hardcoded until the CMS article is placed. The CARDS array mirrors
 * the repeatable Liferay field (cookieCards) — it is only used when the
 * article is not placed (or its cards are empty).
 * TODO(TCS-8180): legal copy pending approval (LGPD/ANPD).
 */
const PREFERENCES_FALLBACK_PROPS = {
  br: {
    TITLE: 'Configuração de cookies',
    SUBTITLE:
      'No Sky+ usamos cookies próprios e de terceiros para análise, publicidade e funcionalidade. Bloqueá-los pode afetar sua experiência. Consulte nossa <a href="/cookies">Política de Cookies</a>.',
    SAVE: 'Salvar e sair',
    DISCLAIMER:
      'Você também pode desabilitar cookies, inclusive os necessários, através do seu navegador. No entanto, isso pode afetar algumas funcionalidades do website.',
    CARDS: [
      {
        id: 1,
        title: 'Essenciais',
        description:
          'Necessários para o funcionamento básico do site. Sempre ativos.',
        gtmName: 'essential',
        badgeLabel: 'Sempre ativos',
        type: 'essential'
      },
      {
        id: 2,
        title: 'Desempenho e analytics',
        description:
          'Nos ajudam a entender como o site é usado, para melhorá-lo.',
        gtmName: 'performance',
        toggleButtonValue: false,
        type: 'optional'
      },
      {
        id: 3,
        title: 'Marketing',
        description:
          'Usados para exibir conteúdo e anúncios relevantes para você.',
        gtmName: 'marketing',
        toggleButtonValue: false,
        type: 'optional'
      }
    ]
  },
  ssla: {
    TITLE: 'Configuración de cookies',
    SUBTITLE:
      'En DGO utilizamos cookies propias y de terceros para análisis, publicidad y funcionalidad. Bloquearlas puede afectar tu experiencia. Consulta nuestra <a href="/cookies">Política de Cookies</a>.',
    SAVE: 'Guardar y salir',
    DISCLAIMER:
      'También puedes deshabilitar las cookies, incluidas las necesarias, desde tu navegador. Sin embargo, esto puede afectar algunas funcionalidades del sitio web.',
    CARDS: [
      {
        id: 1,
        title: 'Esenciales',
        description:
          'Necesarias para el funcionamiento básico del sitio. Siempre activas.',
        gtmName: 'essential',
        badgeLabel: 'Siempre activas',
        toggleButtonValue: true,
        isDisabled: true,
        type: 'essential'
      },
      {
        id: 2,
        title: 'Rendimiento y analíticas',
        description:
          'Nos ayudan a entender cómo se utiliza el sitio para mejorarlo.',
        gtmName: 'performance',
        toggleButtonValue: false,
        type: 'optional'
      },
      {
        id: 3,
        title: 'Marketing',
        description:
          'Se utilizan para mostrar contenido y anuncios relevantes para ti.',
        gtmName: 'marketing',
        toggleButtonValue: false,
        type: 'optional'
      }
    ]
  }
} as const

export default function CookieConsentPreferences() {
  const { brand } = useLocale()

  /** CMS kill-switch: when the article sets it to false, nothing renders */
  const showCookieConsent = useCookieConsentPropsStore.use.showCookieConsent()

  const isPreferencesOpen = useCookieConsentStore.use.isPreferencesOpen()
  const closePreferences = useCookieConsentStore.use.closePreferences()
  const consentsFromStore = useCookieConsentStore.use.consents()

  const { handleSaveConsents } = useCookieConsent()

  const lang = brand === 'sky-mais' ? 'br' : 'ssla'
  const fallback = PREFERENCES_FALLBACK_PROPS[lang]

  /** CMS texts (Liferay article) with hardcoded fallbacks per brand */
  const { configureCookiesModalTexts } = useCookieConsentPropsStore()
  const title = configureCookiesModalTexts.modalTitle || fallback.TITLE
  const subtitle = configureCookiesModalTexts.modalSubtitle || fallback.SUBTITLE
  const saveLabel = configureCookiesModalTexts.modalSaveLabel || fallback.SAVE
  const disclaimer =
    configureCookiesModalTexts.modalDisclaimer || fallback.DISCLAIMER

  /**
   * Consent category cards: a repeatable Liferay field (nested inside the
   * configureCookiesModalTexts group) mapped directly to the design-system
   * CookieCard interface, so the business can add/edit categories without a
   * FE change. Falls back to the hardcoded cards per brand when the CMS
   * article is not placed (or has no cards).
   */
  const cmsCards = configureCookiesModalTexts.cookieCards
  const cards: ICookieConsentCard[] =
    cmsCards.length > 0
      ? cmsCards.map((card) => ({
          ...card,
          // Liferay repeatable ids may arrive as strings — normalize to number
          id: Number(card.id)
        }))
      : (fallback.CARDS as unknown as ICookieConsentCard[])

  /**
   * Seed each card's toggle from the persisted consent state (keyed by the
   * card's gtmName), so reopening the center always shows the current saved
   * state. Essential cards are locked on.
   */
  const cookies: CookieCard[] = cards.map((card) => ({
    ...card,
    isDisabled: card.type === 'essential' ? true : card.isDisabled,
    toggleButtonValue:
      card.type === 'essential'
        ? true
        : card.gtmName in consentsFromStore
        ? Boolean(consentsFromStore[card.gtmName])
        : card.toggleButtonValue ?? false
  }))

  /**
   * ModalConfigCookies handles the draft state internally (toggles are local
   * until the primary CTA is clicked) and calls onSave with the final card
   * values plus a Consent Mode v2 params map derived from each card's
   * gtmName. We persist by gtmName → consent key.
   */
  const handleSave = (savedCards: CookieCard[]) => {
    const consents: Record<string, boolean> = {}

    for (const card of savedCards) {
      if (card.gtmName) {
        consents[card.gtmName] =
          card.type === 'essential' || Boolean(card.toggleButtonValue)
      }
    }

    handleSaveConsents(consents)
  }

  /**
   * Mount the modal only while it is open: ModalConfigCookies captures its
   * internal draft state (useState(cookies)) once per mount, so keeping it
   * mounted with isOpen=false would freeze the fallback cards captured at
   * app mount (before the CMS props hydrate) and onSave would persist the
   * fallback gtmNames. Mounting on open re-seeds the draft from the current
   * CMS cards every time.
   */
  if (!showCookieConsent || !isPreferencesOpen) {
    return null
  }

  return (
    <ModalConfigCookies
      isOpen
      onClose={closePreferences}
      title={title}
      subtitle={subtitle}
      cookies={cookies}
      disclaimer={disclaimer}
      primaryButton={{ children: saveLabel }}
      onSave={handleSave}
    />
  )
}
